/**
 * PDFTemplateEditor Component
 *
 * Visual drag-and-drop editor for positioning form fields on PDF templates.
 * Uses react-pdf to render PDF pages and allows admins to define field locations.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import FieldPropertiesPanel from './FieldPropertiesPanel';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const FIELD_TYPES = [
  { type: 'text', label: 'Text Field', icon: 'T', color: 'bg-blue-500' },
  { type: 'date', label: 'Date Field', icon: 'D', color: 'bg-green-500' },
  { type: 'checkbox', label: 'Checkbox', icon: '☑', color: 'bg-yellow-500' },
  { type: 'signature', label: 'Signature', icon: '✍', color: 'bg-purple-500' },
];

const DEFAULT_FIELD_SIZE = {
  text: { width: 150, height: 24 },
  date: { width: 100, height: 24 },
  checkbox: { width: 20, height: 20 },
  signature: { width: 200, height: 60 },
};

// Generate unique ID
const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export default function PDFTemplateEditor({ template, onSave, onCancel }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [fieldDefinitions, setFieldDefinitions] = useState(
    template.field_definitions || { pages: [], metadata: {} }
  );
  const [selectedField, setSelectedField] = useState(null);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const pageContainerRef = useRef(null);

  // Get fields for current page
  const getCurrentPageFields = useCallback(() => {
    const page = fieldDefinitions.pages?.find((p) => p.pageNumber === currentPage);
    return page?.fields || [];
  }, [fieldDefinitions, currentPage]);

  // Update fields for current page
  const updateCurrentPageFields = useCallback(
    (newFields) => {
      setFieldDefinitions((prev) => {
        const pages = [...(prev.pages || [])];
        const pageIndex = pages.findIndex((p) => p.pageNumber === currentPage);

        if (pageIndex >= 0) {
          pages[pageIndex] = { ...pages[pageIndex], fields: newFields };
        } else {
          pages.push({ pageNumber: currentPage, fields: newFields });
        }

        return { ...prev, pages, metadata: { ...prev.metadata, totalPages: numPages } };
      });
    },
    [currentPage, numPages]
  );

  // Handle PDF load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  // Handle PDF load error
  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF. Please check the file.');
  };

  // Handle drag start from toolbox
  const handleToolboxDragStart = (fieldType) => (e) => {
    setDraggedFieldType(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drop on PDF page
  const handleDrop = (e) => {
    e.preventDefault();

    if (!draggedFieldType || !pageContainerRef.current) return;

    const rect = pageContainerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    const newField = {
      id: generateId(),
      name: `${draggedFieldType}_${Date.now()}`,
      type: draggedFieldType,
      label: `New ${draggedFieldType} field`,
      x: Math.round(x),
      y: Math.round(y),
      ...DEFAULT_FIELD_SIZE[draggedFieldType],
      fontSize: draggedFieldType === 'checkbox' ? 12 : 10,
      required: false,
      dataMapping: '',
    };

    updateCurrentPageFields([...getCurrentPageFields(), newField]);
    setSelectedField(newField.id);
    setDraggedFieldType(null);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Handle field selection
  const handleFieldClick = (e, field) => {
    e.stopPropagation();
    setSelectedField(field.id);
  };

  // Handle field drag start (moving existing field)
  const handleFieldDragStart = (e, field) => {
    e.stopPropagation();
    setIsDragging(true);
    setSelectedField(field.id);

    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Handle field drag (mouse move)
  const handleFieldDrag = useCallback(
    (e) => {
      if (!isDragging || !selectedField || !pageContainerRef.current) return;

      const rect = pageContainerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - dragOffset.x) / scale;
      const y = (e.clientY - rect.top - dragOffset.y) / scale;

      const fields = getCurrentPageFields().map((f) =>
        f.id === selectedField
          ? { ...f, x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) }
          : f
      );

      updateCurrentPageFields(fields);
    },
    [isDragging, selectedField, dragOffset, scale, getCurrentPageFields, updateCurrentPageFields]
  );

  // Handle mouse up (stop dragging)
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleFieldDrag);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleFieldDrag);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleFieldDrag, handleMouseUp]);

  // Handle field update from properties panel
  const handleFieldUpdate = (updatedField) => {
    const fields = getCurrentPageFields().map((f) =>
      f.id === updatedField.id ? updatedField : f
    );
    updateCurrentPageFields(fields);
  };

  // Handle field delete
  const handleFieldDelete = (fieldId) => {
    const fields = getCurrentPageFields().filter((f) => f.id !== fieldId);
    updateCurrentPageFields(fields);
    setSelectedField(null);
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fieldDefinitions);
    } finally {
      setSaving(false);
    }
  };

  // Click outside to deselect
  const handlePageClick = () => {
    setSelectedField(null);
  };

  // Get selected field object
  const selectedFieldObject = getCurrentPageFields().find((f) => f.id === selectedField);

  return (
    <div className="flex h-full">
      {/* Left Toolbox */}
      <div className="w-48 bg-gray-100 border-r p-4 flex flex-col">
        <h3 className="font-semibold mb-4 text-sm">Field Types</h3>
        <p className="text-xs text-gray-500 mb-3">Drag onto PDF</p>

        <div className="space-y-2">
          {FIELD_TYPES.map((ft) => (
            <div
              key={ft.type}
              draggable
              onDragStart={handleToolboxDragStart(ft.type)}
              className={`${ft.color} text-white px-3 py-2 rounded cursor-move flex items-center gap-2 hover:opacity-90`}
            >
              <span className="text-lg">{ft.icon}</span>
              <span className="text-sm">{ft.label}</span>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t">
          <h3 className="font-semibold mb-2 text-sm">Page Info</h3>
          <p className="text-xs text-gray-600">
            Fields on this page: {getCurrentPageFields().length}
          </p>
          <p className="text-xs text-gray-600">
            Total fields: {fieldDefinitions.pages?.reduce((acc, p) => acc + (p.fields?.length || 0), 0) || 0}
          </p>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 flex flex-col bg-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white border-b p-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                &lt;
              </button>
              <span className="text-sm">
                Page {currentPage} of {numPages || '?'}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(numPages || 1, p + 1))}
                disabled={currentPage >= (numPages || 1)}
                className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
              >
                &gt;
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
                className="px-2 py-1 bg-gray-200 rounded"
              >
                -
              </button>
              <span className="text-sm">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale((s) => Math.min(2, s + 0.1))}
                className="px-2 py-1 bg-gray-200 rounded"
              >
                +
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-1 rounded text-white ${
                saving ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {saving ? 'Saving...' : 'Save Fields'}
            </button>
          </div>
        </div>

        {/* PDF Display Area */}
        <div className="flex-1 overflow-auto p-4">
          {pdfError ? (
            <div className="text-center text-red-600 p-8">{pdfError}</div>
          ) : (
            <Document
              file={template.pdf_url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<div className="text-center p-8">Loading PDF...</div>}
            >
              <div
                ref={pageContainerRef}
                className="relative inline-block shadow-lg"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={handlePageClick}
                style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
              >
                <Page
                  pageNumber={currentPage}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />

                {/* Field Overlays */}
                {getCurrentPageFields().map((field) => (
                  <div
                    key={field.id}
                    className={`absolute border-2 cursor-move ${
                      selectedField === field.id
                        ? 'border-blue-600 bg-blue-100 bg-opacity-40'
                        : 'border-gray-400 bg-gray-100 bg-opacity-30 hover:border-blue-400'
                    }`}
                    style={{
                      left: field.x,
                      top: field.y,
                      width: field.width,
                      height: field.height,
                    }}
                    onClick={(e) => handleFieldClick(e, field)}
                    onMouseDown={(e) => handleFieldDragStart(e, field)}
                  >
                    {/* Only show label when selected */}
                    {selectedField === field.id && (
                      <div
                        className={`absolute -top-4 left-0 text-[10px] px-1 rounded ${
                          FIELD_TYPES.find((ft) => ft.type === field.type)?.color || 'bg-gray-500'
                        } text-white whitespace-nowrap pointer-events-none z-10`}
                      >
                        {field.name || field.type}
                      </div>
                    )}
                    {/* Type indicator icon inside field */}
                    <div className="absolute top-0 right-0 text-[8px] px-0.5 bg-black bg-opacity-30 text-white rounded-bl pointer-events-none">
                      {FIELD_TYPES.find((ft) => ft.type === field.type)?.icon || '?'}
                    </div>
                    {field.type === 'signature' && (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-[10px] pointer-events-none">
                        [Sig]
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Document>
          )}
        </div>
      </div>

      {/* Right Properties Panel */}
      <div className="w-64 bg-white border-l">
        <FieldPropertiesPanel
          field={selectedFieldObject}
          onUpdate={handleFieldUpdate}
          onDelete={handleFieldDelete}
        />
      </div>
    </div>
  );
}
